import React, { useEffect, useState, useCallback } from 'react';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MainCard from 'ui-component/cards/MainCard';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import { get, post } from "../../../axios/apiService";
import ActionButtons from 'ui-component/actionButton/ActionButtons';
import PlanAssignmentsDialog from './PlanAssignmentsDialog';
import SearchBar from 'ui-component/SearchBar/SearchBar';
import { Edit, Visibility } from '@mui/icons-material';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

const PlansPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState('');
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.Plan_Management);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const sortField = sortModel.length > 0 ? sortPlanField(sortModel[0].field) : 'CreatedDate';
      const sortOrder = sortModel.length > 0
        ? (sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending')
        : 'Descending';
      const sortQuery = `&$orderby=${sortField}&$orderbydirection=${sortOrder}`;
      const searchQuery = search ? `&$filter=Name~LIKE~${search}` : '';

      const response = await get(
        `Plans?$skip=${paginationModel.page * paginationModel.pageSize}&$top=${paginationModel.pageSize}${searchQuery}${sortQuery}`
      );
      if (!response?.values) {
        setPlans([]);
        setRowCount(0);
        return;
      }
      setRowCount(response.totalCount);
      const enrichedPlans = response.values.map((plan) => {
        const features = plan.featureAssignmentDto || [];
        const calculators = plan.calculatorAssignmentDto || [];
        const tags = [
          ...features.map((f) => f.featureName),
          ...calculators.map((c) => c.calculatorName)
        ];

        return {
          ...plan,
          tags,
          assignments: {
            features,
            calculators
          }
        };
      });

      setPlans(enrichedPlans);
    } catch (err) {
      console.error('Failed to load plans', err);
    } finally {
      setIsLoading(false);
    }
  }, [paginationModel, sortModel, search]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const sortPlanField = (field) => {
    if (field === 'name') return 'Name';
    if (field === 'isActive') return 'IsActive';
    return 'CreatedDate';
  };

  const handleAssignmentsUpdated = useCallback(
    (updatedPlan) => {
      setPlans((prev) =>
        prev.map((p) =>
          p.id === updatedPlan.id
            ? {
              ...p,
              assignments: {
                features: updatedPlan.featureAssignmentDto || [],
                calculators: updatedPlan.calculatorAssignmentDto || []
              },
              tags: [
                ...(updatedPlan.featureAssignmentDto || []).map((f) => f.featureName),
                ...(updatedPlan.calculatorAssignmentDto || []).map((c) => c.calculatorName)
              ]
            }
            : p
        )
      );
    },
    []
  );


  const handleFreeChange = async (row) => {
    try {
      setIsLoading(true);
  
      setPlans(prevPlans => 
        prevPlans.map(p => ({
          ...p,
          isFree: p.id === row.id ? !row.isFree : false 
        }))
      );
  
      await post(`Plans/${row.id}/isfree`, { isFree: !row.isFree });
  
    } catch (err) {
      console.error('Failed to update free status', err);
        setPlans(prevPlans => 
        prevPlans.map(p => ({
          ...p,
          isFree: p.id === row.id ? row.isFree : p.isFree
        }))
      );
  
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleEdit = useCallback((row) => {
    setSelectedPlan(row);
    setOpenDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
    setSelectedPlan(null);
  }, []);


  const columns = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'isFree',
      headerName: 'Is Free Plan',
      width: 130,
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={params.value || false}
          onChange={() => handleFreeChange(params.row)}
          style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
          disabled={!canEdit}
        />
      )
    },
    {
      field: 'tags',
      headerName: 'Assigned',
      flex: 1,
      renderCell: (params) => {
        const { assignments } = params.row || {};
        const calculatorNames = assignments?.calculators?.map(c => c.calculatorName) || [];
        const tags = params.value || [];

        if (tags.length === 0) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              sx={{ height: '100%' }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontStyle: 'italic' }}
              >
                No assignments yet
              </Typography>
            </Stack>
          );
        }

        return (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ flexWrap: 'wrap', height: '100%', overflow: 'hidden' }}>
            {tags.slice(0, 2).map((tag, idx) => {
              const isCalculator = calculatorNames.includes(tag);

              return (
                <Chip
                  key={idx}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: isCalculator ? '#FF8040' : '#8ABB6C',
                    color: '#fff',
                  }} />
              )
            })}
            {tags.length > 3 && (
              <Chip label={`+${tags.length - 3}`} size="small" variant="outlined" />
            )}
          </Stack>
        );
      }
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'assignment',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <ActionButtons
          actions={[
            {
              tooltip: "Edit",
              icon: canEdit ? <Edit /> : <Visibility/>,
              color: "primary",
              onClick: () => handleEdit(params.row),
            }
          ]}
        />
      ),
    }
    
  ];

  return (
    <Box>
      <MainCard title="Plan Management">
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <SearchBar
              value={search}
              onChange={handleSearchChange}
              placeholder="Search"
            />
          </Grid>

          <Grid item xs={12}>
            <DataGrid
              rows={plans}
              columns={columns}
              getRowId={(row) => row.id}
              disableSelectionOnClick
              autoHeight
              paginationMode="server"
              sortingMode="server"
              rowCount={rowCount}
              pageSizeOptions={[10]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
            />
          </Grid>
        </Grid>
      </MainCard>

      {selectedPlan && (
        <PlanAssignmentsDialog
          open={openDialog}
          plan={selectedPlan}
          onClose={handleDialogClose}
          onAssignmentsUpdated={handleAssignmentsUpdated}
          canEdit={canEdit}
        />
      )}

      <LoadingBackdrop open={isLoading} />
    </Box>
  );
};

export default PlansPage;
